import React, { memo } from "react";
import Link from "@qlik/qlik/src/components/Link";
import * as styles from "./styles.module.css";
import isNoItem from "@qlik/qlik/src/lib/util/isNoItem";

/**
 * FooterMenuItem component
 * This component renders a menu item in the footer.
 * Each menu item can have subItems and can be either a link or a simple text.
 * @param {Object} menuItem - The menu item to be rendered.
 * @param {string} className - Additional CSS classes for the menu item.
 * @param {string} type - The type of the menu item. Can be 'horizontal' or 'vertical'.
 * @param {Function} setExpandedMenuId - Function to set the expanded menu item id.
 * @param {boolean} expanded - Whether the menu item is expanded or not.
 */
const FooterMenuItem = memo(({ menuItem, setExpandedMenuId, expanded = false }) => {
    const linkClasses = "text-base w-fit text-ocean font-semibold";

    const { pageLink, externalUrl, title, subItems } = menuItem;
    const hasLink = externalUrl || pageLink;

    /**
     * Toggle the expansion of the menu item.
     * @param {Event} e - The click event.
     */
    const toggleExpand = (e) => {
        if (!subItems?.length || e.target.tagName.toLowerCase() === "a") return;

        setExpandedMenuId(expanded ? null : menuItem.id);
    };

    return (
        <li className="border-opacity-50 border-stone lg:py-7.5 lg:border-b last-of-type:border-0">
            <button
                className={`relative leading-tight ${!hasLink && " cursor-default"} ${subItems ? " flex" : null}`}
                onClick={toggleExpand}
            >
                {hasLink ? (
                    <Link to={pageLink?.slug ?? externalUrl} className={`${linkClasses} hover:text-green`}>
                        {title}
                    </Link>
                ) : (
                    <span className={linkClasses}>{title}</span>
                )}
                {subItems && (
                    <i
                        className={`green-dropdown-small lg:hidden transition-all ml-auto mt-1.25 ${
                            expanded ? "rotate-0 -mr-1.25" : "rotate-180 grayscale"
                        }`}
                    />
                )}
            </button>
            {subItems && (
                <ul
                    className={`flex flex-col gap-1.25 lg:h-auto lg:pt-4.5 overflow-hidden transition-all ${
                        expanded ? "pt-5" : "h-0"
                    }`}
                >
                    {subItems
                        ?.filter((subItem) => !isNoItem(subItem?.title))
                        .map((subItem) => (
                            <li
                                key={subItem.id}
                                className={` ${subItem?.subItems ? "mt-4.5 lg:mt-3.25" : ""} first-of-type:mt-0`}
                            >
                                <p
                                    className={` text-sm leading-snug ${styles.footerCustomLinHeight1} ${
                                        subItem?.subItems
                                            ? `text-xs font-semibold leading-snug !tracking-[1.2px] text-blue pointer-events-none ${styles.footerCustomLinHeight2}`
                                            : ""
                                    } `}
                                >
                                    <Link
                                        to={subItem?.pageLink?.slug ?? subItem?.externalUrl}
                                        className="inline-block align-top w-fit hover:text-green"
                                    >
                                        {subItem?.title}
                                    </Link>
                                </p>
                                {subItem?.subItems && (
                                    <ul className="flex flex-col gap-1.25 pt-1.75 lg:h-auto overflow-hidden transition-all">
                                        {subItem?.subItems
                                            ?.filter((x) => !x?.title?.toLowerCase()?.startsWith("[[noitem]]"))
                                            .map((subItemSub) => (
                                                <li key={subItemSub.id}>
                                                    <p
                                                        className={`text-sm leading-snug ${styles.footerCustomLinHeight1}`}
                                                    >
                                                        <Link
                                                            to={subItemSub?.pageLink?.slug ?? subItemSub?.externalUrl}
                                                            className="inline-block align-top w-fit hover:text-green"
                                                        >
                                                            {subItemSub?.title}
                                                        </Link>
                                                    </p>
                                                </li>
                                            ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                </ul>
            )}
        </li>
    );
});

export default FooterMenuItem;
